import type { Key } from 'react';
import { useEffect, useState } from 'react';
import { MapPin } from 'lucide-react';
import ReactSelect, { type StylesConfig, type GroupBase } from 'react-select';
import locationService from '../../../services/locationService';

type Option = { label: string; value: string };

type Props = {
  locationId: string;
  setLocationId: (v: string) => void;
  setPage: (n: number) => void;
};

/* ─── pill-style react-select styles ──────────────────────────────────────── */
function buildStyles(hasValue: boolean): StylesConfig<Option, false, GroupBase<Option>> {
  return {
    control: (base, state) => ({
      ...base,
      minHeight: '34px',
      height: '34px',
      minWidth: '170px',
      borderRadius: '9999px',
      background: hasValue ? '#006346' : '#ffffff',
      border: hasValue
        ? '1px solid #006346'
        : state.isFocused
          ? '1px solid #006346'
          : '1px solid #d1d5db',
      boxShadow: state.isFocused
        ? '0 0 0 3px rgba(166,206,57,0.22)'
        : hasValue
          ? '0 2px 8px -2px rgba(0,99,70,0.35)'
          : 'none',
      cursor: 'pointer',
      transition: 'all 0.15s',
      flexWrap: 'nowrap',
      '&:hover': {
        border: hasValue ? '1px solid #005233' : '1px solid #9ca3af',
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 6px 0 12px',
      flexWrap: 'nowrap',
      overflow: 'hidden',
    }),
    singleValue: (base) => ({
      ...base,
      color: '#ffffff',
      fontSize: '11px',
      fontWeight: '700',
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: '200px',
    }),
    placeholder: (base) => ({
      ...base,
      color: '#6b7280',
      fontSize: '11px',
      fontWeight: '700',
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
    }),
    input: (base) => ({
      ...base,
      color: hasValue ? '#ffffff' : '#111827',
      fontSize: '11px',
      margin: 0,
      padding: 0,
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: '0 10px 0 2px',
      color: hasValue ? 'rgba(255,255,255,0.75)' : '#9ca3af',
      '&:hover': { color: hasValue ? '#ffffff' : '#374151' },
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: '0 6px 0 2px',
      color: hasValue ? 'rgba(255,255,255,0.75)' : '#9ca3af',
      '&:hover': { color: hasValue ? '#ffffff' : '#374151' },
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    menu: (base) => ({
      ...base,
      minWidth: '260px',
      width: 'max-content',
      maxWidth: '400px',
      borderRadius: '14px',
      border: '1px solid #e4dfd6',
      boxShadow:
        '0 12px 32px -6px rgba(0,99,70,0.18), 0 4px 12px -3px rgba(0,0,0,0.08)',
      overflow: 'hidden',
      marginTop: '6px',
      padding: '4px',
    }),
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
    menuList: (base) => ({
      ...base,
      padding: '2px',
      maxHeight: '240px',
      overflowY: 'auto',
      scrollbarWidth: 'thin',
    }),
    option: (base, state) => ({
      ...base,
      fontSize: '13px',
      fontWeight: state.isSelected ? '600' : '500',
      padding: '8px 14px',
      borderRadius: '8px',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      backgroundColor: state.isSelected
        ? '#006346'
        : state.isFocused
          ? 'rgba(0,99,70,0.07)'
          : 'transparent',
      color: state.isSelected ? '#ffffff' : '#111827',
      transition: 'background 0.1s',
      '&:active': { backgroundColor: 'rgba(0,99,70,0.14)' },
    }),
    noOptionsMessage: (base) => ({
      ...base,
      fontSize: '12px',
      color: '#9ca3af',
      padding: '10px 14px',
    }),
    loadingMessage: (base) => ({
      ...base,
      fontSize: '12px',
      color: '#9ca3af',
    }),
  };
}

/* ─── component ───────────────────────────────────────────────────────────── */
export default function EpcProjectFilterBar({ locationId, setLocationId, setPage }: Props) {
  const [locationOptions, setLocationOptions] = useState<Option[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  useEffect(() => {
    const loadLoc = async () => {
      setLoadingLocations(true);
      try {
        const locs = await locationService.getLocationsForDropdown();
        setLocationOptions(
          locs.map((loc: { _id: string; id?: Key; name: string }) => ({
            value: String(loc._id ?? loc.id ?? ''),
            label: loc.name,
          })),
        );
      } catch {
        /* ignore */
      } finally {
        setLoadingLocations(false);
      }
    };
    void loadLoc();
  }, []);

  const selectedOption = locationOptions.find((o) => o.value === locationId) ?? null;
  const hasValue = Boolean(selectedOption);

  return (
    <div className="flex items-center gap-2">
      <MapPin
        className="h-3.5 w-3.5 shrink-0"
        strokeWidth={2.2}
        style={{ color: hasValue ? '#006346' : '#9ca3af' }}
      />
      <ReactSelect<Option, false, GroupBase<Option>>
        options={locationOptions}
        value={selectedOption}
        onChange={(opt) => {
          setLocationId(opt?.value ?? '');
          setPage(1);
        }}
        styles={buildStyles(hasValue)}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        placeholder="All locations"
        isClearable={hasValue}
        isSearchable
        isLoading={loadingLocations}
        isDisabled={loadingLocations}
        noOptionsMessage={() => 'No locations found'}
        loadingMessage={() => 'Loading locations…'}
      />
    </div>
  );
}
